/*
 * Reine Logik des Registrierportals, ohne DOM, damit sie sich mit node:test
 * prüfen lässt.
 */

import type { PortalDocumentDto } from './types';

export type Route =
  | { view: 'register'; token: string }
  | { view: 'confirm'; code: string }
  | { view: 'home' };

/** Token und Code stehen im Fragment (#e=..., #b=...), nie im Pfad: Fragmente gehen nicht an den Server. */
export function parseRoute(hash: string): Route {
  const params = new URLSearchParams(hash.replace(/^#/, ''));
  const token = params.get('e');
  if (token && /^[A-Za-z0-9_-]{43}$/.test(token)) return { view: 'register', token };
  const code = params.get('b');
  if (code && /^[A-Za-z0-9_-]{43}$/.test(code)) return { view: 'confirm', code };
  return { view: 'home' };
}

export interface FormValues {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  accepted: ReadonlySet<string>;
}

/** Fehlertext für das Formular, oder null. Der Server prüft dasselbe noch einmal. */
export function validate(values: FormValues, documents: readonly PortalDocumentDto[]): string | null {
  if (!values.firstName.trim() || !values.lastName.trim()) return 'Bitte Vor- und Nachnamen angeben.';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email.trim())) return 'Bitte eine gültige E-Mail-Adresse angeben.';
  if (values.phone.trim() && !/^[0-9+ ()/-]{3,40}$/.test(values.phone.trim())) return 'Die Telefonnummer enthält ungültige Zeichen.';
  const missing = documents.find((d) => d.consentMode === 'required' && !values.accepted.has(d.versionId));
  if (missing) return `Bitte „${missing.title}“ zustimmen.`;
  return null;
}

/** Dokumente mit Zustimmung, Pflicht zuerst. */
export function consentDocuments(documents: readonly PortalDocumentDto[]): PortalDocumentDto[] {
  return documents
    .filter((d) => d.consentMode !== 'none')
    .sort((a, b) => (a.consentMode === b.consentMode ? 0 : a.consentMode === 'required' ? -1 : 1));
}

/** Kontrast-taugliche Schriftfarbe auf einer Hintergrundfarbe (#rrggbb), falls keine angegeben ist. */
export function readableOn(hex: string): string {
  const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex.length === 4
    ? `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}` : hex);
  if (!m) return '#ffffff';
  const [r, g, b] = [m[1], m[2], m[3]].map((x) => parseInt(x!, 16) / 255).map((c) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4));
  const lum = 0.2126 * r! + 0.7152 * g! + 0.0722 * b!;
  return lum > 0.45 ? '#16191d' : '#ffffff';
}
